import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Download, Users, DollarSign, Calendar, ArrowUpDown, AlertTriangle, Building2 } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const statCard = 'bg-[#0F0F0F] border border-[#2A2A2A] rounded-lg p-4 text-center';

function fmt$(v) { return v != null ? `$${Number(v).toFixed(2)}` : '—'; }
function fmtNum(v) { return v != null ? Number(v).toLocaleString() : '—'; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; }

function today() { return new Date().toISOString().slice(0, 10); }
function weekAgo() { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); }
function monthStart() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }
function lastMonthRange() {
  const s = new Date(); s.setMonth(s.getMonth() - 1); s.setDate(1);
  const e = new Date(s.getFullYear(), s.getMonth() + 1, 0);
  return [s.toISOString().slice(0, 10), e.toISOString().slice(0, 10)];
}

const PERIODS = [
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

export default function UsageReporting({ workspaceId }) {
  const [period, setPeriod] = useState('month');
  const [customStart, setCustomStart] = useState(weekAgo());
  const [customEnd, setCustomEnd] = useState(today());
  const [costs, setCosts] = useState(null);
  const [departments, setDepartments] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sortCol, setSortCol] = useState('cost');
  const [sortAsc, setSortAsc] = useState(false);

  const dateParams = useMemo(() => {
    switch (period) {
      case 'week': return { period: 'week', start_date: weekAgo(), end_date: today() };
      case 'month': return { period: 'month', start_date: monthStart(), end_date: today() };
      case 'last_month': { const [s, e] = lastMonthRange(); return { period: 'custom', start_date: s, end_date: e }; }
      case 'custom': return { period: 'custom', start_date: customStart, end_date: customEnd };
      default: return { period: 'month', start_date: monthStart(), end_date: today() };
    }
  }, [period, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [costRes, deptRes, alertRes] = await Promise.all([
        API.get('/api/enterprise/costs', { params: { workspace_id: workspaceId, ...dateParams } }),
        API.get('/api/enterprise/costs/by-department', { params: { workspace_id: workspaceId } }).catch(() => null),
        API.get('/api/enterprise/budgets/alerts', { params: { workspace_id: workspaceId } }).catch(() => null),
      ]);
      setCosts(costRes.data);
      setDepartments(deptRes?.data || null);
      setAlerts(alertRes?.data?.alerts || []);
    } catch { /* fail gracefully */ }
    setLoading(false);
  }, [workspaceId, dateParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await API.get('/api/enterprise/costs/export', {
        params: { workspace_id: workspaceId, format: 'csv', ...dateParams },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fray-usage-${dateParams.start_date}-to-${dateParams.end_date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
    setExporting(false);
  };

  const userRows = useMemo(() => {
    const rows = costs?.users || costs?.per_user || [];
    return [...rows].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortAsc ? cmp : -cmp;
    });
  }, [costs, sortCol, sortAsc]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };

  const providers = costs?.providers || costs?.per_provider || [];
  const maxProviderSpend = Math.max(...providers.map(p => p.cost || p.spend || 0), 1);

  const summary = costs?.summary || costs || {};
  const totalSpend = summary.total_cost ?? summary.total_spend ?? 0;
  const totalFrays = summary.total_frays ?? summary.fray_count ?? 0;
  const totalTokens = summary.total_tokens ?? summary.token_count ?? 0;
  const activeUsers = summary.active_users ?? summary.user_count ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF4D00] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Budget Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#A61E17]/20 border border-[#A61E17]/40 rounded-lg px-4 py-2 text-sm">
              <AlertTriangle size={16} className="text-[#A61E17] shrink-0" />
              <span className="text-[#F5F1E8]">{a.message || `Budget alert: ${a.type}`}</span>
            </div>
          ))}
        </div>
      )}

      {/* Date Range Selector */}
      <div className={`${card} flex flex-wrap items-center gap-3`}>
        <Calendar size={16} className="text-[#A0A0A0]" />
        <span className="text-sm text-[#A0A0A0] mr-1">Period:</span>
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded text-sm transition-colors ${
              period === p.key
                ? 'bg-[#FF4D00] text-white'
                : 'bg-[#0F0F0F] text-[#A0A0A0] hover:text-[#F5F1E8] border border-[#2A2A2A]'
            }`}
          >
            {p.label}
          </button>
        ))}
        {period === 'custom' && (
          <>
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm text-[#F5F1E8] font-mono"
            />
            <span className="text-[#A0A0A0]">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="bg-[#0F0F0F] border border-[#2A2A2A] rounded px-2 py-1.5 text-sm text-[#F5F1E8] font-mono"
            />
          </>
        )}
        <div className="ml-auto">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm text-[#F5F1E8] hover:border-[#FF4D00] transition-colors disabled:opacity-50"
          >
            <Download size={14} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={statCard}>
          <DollarSign size={20} className="text-[#22C55E] mx-auto mb-2" />
          <div className="text-2xl font-bold text-[#F5F1E8] font-mono">{fmt$(totalSpend)}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">Total Spend</div>
        </div>
        <div className={statCard}>
          <BarChart3 size={20} className="text-[#00D4FF] mx-auto mb-2" />
          <div className="text-2xl font-bold text-[#F5F1E8] font-mono">{fmtNum(totalFrays)}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">Total Frays</div>
        </div>
        <div className={statCard}>
          <div className="text-[#F2A900] mx-auto mb-2 font-mono text-lg font-bold">T</div>
          <div className="text-2xl font-bold text-[#F5F1E8] font-mono">{fmtNum(totalTokens)}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">Total Tokens</div>
        </div>
        <div className={statCard}>
          <Users size={20} className="text-[#FF4D00] mx-auto mb-2" />
          <div className="text-2xl font-bold text-[#F5F1E8] font-mono">{fmtNum(activeUsers)}</div>
          <div className="text-xs text-[#A0A0A0] mt-1">Active Users</div>
        </div>
      </div>

      {/* Per-User Usage Table */}
      <div className={card}>
        <h3 className="text-sm font-semibold text-[#F5F1E8] font-['Barlow_Condensed'] uppercase tracking-wide mb-3 flex items-center gap-2">
          <Users size={16} className="text-[#00D4FF]" />
          Per-User Usage
        </h3>
        {userRows.length === 0 ? (
          <p className="text-sm text-[#A0A0A0] py-4 text-center">No user data for this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A] text-[#A0A0A0] text-left">
                  {[
                    { key: 'name', label: 'User' },
                    { key: 'email', label: 'Email' },
                    { key: 'department', label: 'Department' },
                    { key: 'frays', label: 'Frays Run' },
                    { key: 'tokens', label: 'Tokens Used' },
                    { key: 'cost', label: 'Cost ($)' },
                    { key: 'last_active', label: 'Last Active' },
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="py-2 px-2 cursor-pointer hover:text-[#F5F1E8] transition-colors select-none whitespace-nowrap"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <ArrowUpDown size={12} className={sortCol === col.key ? 'text-[#FF4D00]' : 'opacity-30'} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {userRows.map((u, i) => (
                  <tr key={u.user_id || i} className="border-b border-[#2A2A2A]/50 hover:bg-[#0F0F0F] transition-colors">
                    <td className="py-2 px-2 text-[#F5F1E8]">{u.name || u.display_name || '—'}</td>
                    <td className="py-2 px-2 text-[#A0A0A0]">{u.email || '—'}</td>
                    <td className="py-2 px-2 text-[#A0A0A0]">{u.department || '(unassigned)'}</td>
                    <td className="py-2 px-2 font-mono text-[#00D4FF]">{fmtNum(u.frays ?? u.fray_count)}</td>
                    <td className="py-2 px-2 font-mono text-[#F2A900]">{fmtNum(u.tokens ?? u.token_count)}</td>
                    <td className="py-2 px-2 font-mono text-[#22C55E]">{fmt$(u.cost ?? u.spend)}</td>
                    <td className="py-2 px-2 font-mono text-[#A0A0A0]">{fmtDate(u.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per-Provider Breakdown */}
      {providers.length > 0 && (
        <div className={card}>
          <h3 className="text-sm font-semibold text-[#F5F1E8] font-['Barlow_Condensed'] uppercase tracking-wide mb-3 flex items-center gap-2">
            <BarChart3 size={16} className="text-[#F2A900]" />
            Spend by Provider
          </h3>
          <div className="space-y-3">
            {providers.map((p, i) => {
              const spend = p.cost || p.spend || 0;
              const pct = (spend / maxProviderSpend) * 100;
              const colors = ['#00D4FF', '#FF4D00', '#F2A900', '#22C55E', '#A061FA'];
              const color = colors[i % colors.length];
              return (
                <div key={p.provider || i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#F5F1E8]">{p.provider || p.name || 'Unknown'}</span>
                    <span className="font-mono text-[#A0A0A0]">{fmt$(spend)}</span>
                  </div>
                  <div className="h-2 bg-[#0F0F0F] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-Department Breakdown */}
      {departments?.departments?.length > 0 && (
        <div className={card}>
          <h3 className="text-sm font-semibold text-[#F5F1E8] font-['Barlow_Condensed'] uppercase tracking-wide mb-3 flex items-center gap-2">
            <Building2 size={16} className="text-[#00D4FF]" />
            Cost by Department
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A] text-[#A0A0A0] text-left">
                  <th className="py-2 px-2">Department</th>
                  <th className="py-2 px-2">Users</th>
                  <th className="py-2 px-2">Frays</th>
                  <th className="py-2 px-2">Tokens</th>
                  <th className="py-2 px-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {departments.departments.map((d, i) => (
                  <tr key={d.department || i} className="border-b border-[#2A2A2A]/50 hover:bg-[#0F0F0F] transition-colors">
                    <td className="py-2 px-2 text-[#F5F1E8]">{d.department || d.name || '—'}</td>
                    <td className="py-2 px-2 font-mono text-[#A0A0A0]">{fmtNum(d.user_count)}</td>
                    <td className="py-2 px-2 font-mono text-[#00D4FF]">{fmtNum(d.frays ?? d.fray_count)}</td>
                    <td className="py-2 px-2 font-mono text-[#F2A900]">{fmtNum(d.tokens ?? d.token_count)}</td>
                    <td className="py-2 px-2 font-mono text-[#22C55E]">{fmt$(d.cost ?? d.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
